import { useRef, useState } from 'react'
import { api, download, query, upload } from '../api/client'
import { useQuery } from '../api/hooks'
import type { Attachment, AttachmentTarget } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { Empty, ErrorBanner, Loading, formatDateTime } from './ui'

/**
 * The permission each target demands, mirroring `AttachmentTargets` on the server.
 *
 * An attachment has no permission of its own: a scan on an entry is as confidential as the entry.
 * This is a courtesy so the buttons match what the server will allow — the server derives the same
 * mapping and checks it on every call.
 */
const GATES: Record<AttachmentTarget, { view: string; write: string }> = {
  DocumentIntake: { view: P.documents.view, write: P.documents.edit },
  JournalEntry: { view: P.journal.view, write: P.journal.edit },
  CashBookEntry: { view: P.cashBook.view, write: P.cashBook.edit },
}

/** What the server accepts. Offered to the file dialog so the wrong file is not chosen at all. */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp,.xlsx,.xls,.docx,.doc'

/**
 * Files attached to one document, entry or book row.
 *
 * Dropped into whichever screen owns the thing being attached to, rather than being a page of its
 * own: an attachment is only ever looked at next to what it supports, and a list of every scan in
 * the office answers no question anybody has.
 */
export function Attachments({
  entityType,
  entityId,
  hint,
}: {
  entityType: AttachmentTarget
  entityId: string
  hint?: string
}) {
  const { can } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const fileInput = useRef<HTMLInputElement>(null)

  const attachments = useQuery<Attachment[]>(
    `/attachments${query({ entityType, entityId })}`,
    [entityType, entityId],
  )

  const gate = GATES[entityType]
  const canWrite = can(gate.write)

  async function send(file: File) {
    setBusy(true)
    setError(undefined)

    try {
      const form = new FormData()
      form.append('entityType', entityType)
      form.append('entityId', entityId)
      form.append('file', file)

      await upload('/attachments', form)
      attachments.reload()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)

      // Cleared so choosing the same file again still fires a change event — the usual second
      // attempt after a refusal is the same file, and without this nothing happens.
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  async function remove(attachment: Attachment) {
    setBusy(true)
    setError(undefined)

    try {
      await api(`/attachments/${attachment.id}`, { method: 'DELETE' })
      attachments.reload()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />
      <ErrorBanner error={attachments.error} />
      {attachments.loading && <Loading what="Loading attachments" />}

      {attachments.data?.length === 0 && <Empty>Nothing attached yet.</Empty>}

      {attachments.data && attachments.data.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th style={{ width: 90 }} className="num">
                  Size
                </th>
                <th style={{ width: 150 }}>Uploaded by</th>
                <th style={{ width: 140 }}>When</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {attachments.data.map((attachment) => (
                <tr key={attachment.id}>
                  <td className="strong">{attachment.fileName}</td>
                  <td className="num soft">{fileSize(attachment.sizeBytes)}</td>
                  <td className="soft">{attachment.uploadedBy ?? '—'}</td>
                  <td className="soft">{formatDateTime(attachment.uploadedAt)}</td>
                  <td>
                    <button
                      className="subtle small"
                      onClick={() =>
                        void download(`/attachments/${attachment.id}/content`, attachment.fileName)
                      }
                    >
                      Download
                    </button>
                    {canWrite && (
                      <button
                        className="subtle small"
                        disabled={busy}
                        onClick={() => void remove(attachment)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && (
        <div style={{ marginTop: 10 }}>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            disabled={busy}
            style={{ width: 'auto' }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void send(file)
            }}
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {busy ? 'Uploading…' : (hint ?? 'PDFs, scans and Office documents, up to 20 MB each.')}
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Bytes as the office would say them. Deliberately not the money formatter — this is a file size,
 * and printing it with two decimals and thousands separators would read as an amount.
 */
function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
